export type FormMethodsLike = {
  watch?: (callback: (values: unknown, info: { name?: string }) => void) => {
    unsubscribe: () => void;
  };
  getValues?: (name?: string) => unknown;
  setValue?: (name: string, value: unknown) => void;
};

export type FormFieldWatchRule = {
  match: string | RegExp;
  onChange: (params: {
    changedName: string;
    values: unknown;
    formMethods: FormMethodsLike;
    match: RegExpMatchArray | null;
  }) => void;
};

export function setupFormFieldWatcher(params: {
  getFormMethods: () => FormMethodsLike | undefined;
  rules: FormFieldWatchRule[];
  pollIntervalMs?: number;
}): () => void {
  const { getFormMethods, rules, pollIntervalMs = 50 } = params;

  let disposed = false;
  let initTimer: number | undefined;
  let unsubscribe: (() => void) | undefined;

  const trySubscribe = () => {
    if (disposed) return;

    const formMethods = getFormMethods();
    if (!formMethods?.watch) {
      initTimer = window.setTimeout(trySubscribe, pollIntervalMs);
      return;
    }

    const subscription = formMethods.watch((values, info) => {
      const changedName = info?.name;
      if (!changedName) return;

      for (const rule of rules) {
        if (typeof rule.match === "string") {
          if (changedName !== rule.match) continue;
          rule.onChange({ changedName, values, formMethods, match: null });
          continue;
        }

        const regexMatch = changedName.match(rule.match);
        if (!regexMatch) continue;

        rule.onChange({
          changedName,
          values,
          formMethods,
          match: regexMatch,
        });
      }
    });

    unsubscribe = () => subscription.unsubscribe();
  };

  trySubscribe();

  return () => {
    disposed = true;
    unsubscribe?.();
    if (initTimer != null) {
      window.clearTimeout(initTimer);
    }
  };
}
